import {
  createContext,
  useContext,
  useContextSelector,
} from "use-context-selector";
import {
  displayAudienceSize,
  getAudienceSizePermission,
  useCalculateAudienceSize,
} from "src/utils/use-calculate-audience-size";
import {
  AndCondition,
  ConditionType,
  OrCondition,
  RootCondition,
  VisualQueryFilter,
} from "src/types/visual";
import { AudienceIcon, RefreshIcon, Row, Tooltip } from "@hightouch/htui";
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  hasQueryChanged,
  Query,
  QueryMetadata,
} from "src/components/audiences/use-last-used-query";
import { LoadingWithHoverCancel } from "src/components/audiences/loading-with-cancel-on-hover";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useResourcePermission } from "src/components/permission/use-resource-permission";

const MAX_CONCURRENT = 10;

export interface AudienceWaterfallListeners {
  calculate: () => Promise<void>;
  cancel: () => void;
  setQueued: (isQueued: boolean) => void;
}

interface AudienceWaterfallContextValue {
  enabled: boolean;
  audienceModelId?: string | number;
  destinationId?: string;
  subsetIds?: string[];
  isFastQuery: boolean;
  sourceId?: string | number;
  register: AudienceWaterfallRegister;
  calculateAllWaterfall: () => void;
  cancelAllWaterfall: () => void;
  calcDidComplete: (id: string) => void;
}

const AudienceWaterfallContext = createContext<
  AudienceWaterfallContextValue | undefined
>(undefined);

interface AudienceWaterfallProviderProps extends QueryMetadata {
  enabled?: boolean;
  calculateOverallAudienceSize: () => void;
  audienceModelId?: string | number;
  sourceId?: string | number;
  children?: React.ReactNode;
}

export const AudienceWaterfallProvider = (
  props: AudienceWaterfallProviderProps,
) => {
  const {
    children,
    calculateOverallAudienceSize,
    enabled = true,
    ...rest
  } = props;
  const [audienceWaterfallListeners, setAudienceWaterfallListeners] = useState<
    Record<
      string,
      {
        listeners: React.MutableRefObject<AudienceWaterfallListeners>;
        index: number;
      }
    >
  >({});
  const [queue, setQueue] = useState<string[]>([]);
  const [runningJobs, setRunningJobs] = useState<string[]>([]);

  // Use a ref to get the latest listeners in the effect without re-triggering it
  const listenersRef = useRef(audienceWaterfallListeners);
  listenersRef.current = audienceWaterfallListeners;

  const calcDidComplete = useCallback((jobId: string) => {
    setRunningJobs((prev) => prev.filter((id) => id !== jobId));
  }, []);

  useEffect(() => {
    const availableSlots = MAX_CONCURRENT - runningJobs.length;
    if (availableSlots <= 0 || queue.length === 0) {
      return;
    }

    const jobsToStart = queue.slice(0, availableSlots);
    setQueue((prev) => prev.slice(availableSlots));
    setRunningJobs((prev) => [...prev, ...jobsToStart]);

    jobsToStart.forEach((jobId) => {
      const job = listenersRef.current[jobId];
      if (job) {
        // Mark as no longer queued, now it's running
        job.listeners.current?.setQueued(false);
        // The calculate function is fully async. Its returned promise does NOT wait until calculation completion.
        // We must poll isLoading/data for completion.
        job.listeners.current?.calculate();
      }
    });
  }, [queue, runningJobs.length]);

  const registerWaterfall = useCallback(
    (
      listeners: React.MutableRefObject<AudienceWaterfallListeners>,
      id: string,
      index: number,
    ) => {
      setAudienceWaterfallListeners((prev) => ({
        ...prev,
        [id]: { listeners, index },
      }));
      return () => {
        setAudienceWaterfallListeners((prev) => {
          const newListeners = { ...prev };
          delete newListeners[id];
          return newListeners;
        });
      };
    },
    [],
  );

  const calculateAllWaterfall = useCallback(() => {
    calculateOverallAudienceSize();

    const sortedListenerIds = Object.entries(audienceWaterfallListeners)
      .sort(([, a], [, b]) => a.index - b.index)
      .map(([id]) => id);

    sortedListenerIds.forEach((id) => {
      audienceWaterfallListeners[id]?.listeners.current.setQueued(true);
    });

    setQueue(sortedListenerIds);
  }, [audienceWaterfallListeners, calculateOverallAudienceSize]);

  const cancelAllWaterfall = useCallback(() => {
    const allJobIds = [...runningJobs, ...queue];
    allJobIds.forEach((jobId) => {
      const job = audienceWaterfallListeners[jobId];
      if (job) {
        job.listeners.current?.cancel();
        job.listeners.current?.setQueued(false);
      }
    });

    setQueue([]);
    setRunningJobs([]);
  }, [audienceWaterfallListeners, queue, runningJobs]);

  return (
    <AudienceWaterfallContext.Provider
      value={{
        ...rest,
        enabled,
        calculateAllWaterfall,
        cancelAllWaterfall,
        register: registerWaterfall,
        calcDidComplete,
      }}
    >
      {children}
    </AudienceWaterfallContext.Provider>
  );
};

const useAudienceWaterfall = () => useContext(AudienceWaterfallContext);

export type AudienceWaterfallRegister = (
  listeners: React.MutableRefObject<AudienceWaterfallListeners>,
  /** Generate this with useId */
  id: string,
  index: number,
) => () => void;

interface AudienceWaterfallProps {
  audienceId: string | number;
  parentModelId: string | number;
  condition: AndCondition<RootCondition> | OrCondition<RootCondition>;
  index: number;
}

const AudienceWaterfallInternal = (
  props: AudienceWaterfallProps & AudienceWaterfallContextValue,
) => {
  const {
    audienceId,
    audienceModelId,
    parentModelId,
    sourceId,
    condition: conditionProp,
    destinationId,
    subsetIds,
    isFastQuery,
    register,
    calculateAllWaterfall,
    calcDidComplete,
    cancelAllWaterfall,
    index,
  } = props;
  const [previousRunData, setPreviousRunData] = useState<Query | undefined>(
    undefined,
  );
  const [isQueued, setIsQueued] = useState(false);
  const isCanceledRef = useRef(false);
  const myId = useId();
  const { isPermitted } = useResourcePermission(
    getAudienceSizePermission({ parentModelId, modelId: audienceModelId }),
  );

  const filter: VisualQueryFilter = useMemo(
    () => ({
      // Waterfalls ignore the overall size cap
      sizeCap: undefined,
      conditions: [
        {
          ...conditionProp,
          conditions: conditionProp.conditions.slice(0, index + 1),
        },
      ],
    }),
    [conditionProp, index],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const nextRunData = useMemo(
    () => ({
      destinationId,
      subsetIds,
      isFastQuery,
      filter,
    }),
    // Using `JSON.stringify` to in-place memoize subsetIds
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression
    [destinationId, isFastQuery, filter, JSON.stringify(subsetIds)],
  );
  const isDirty = useMemo(() => {
    if (!previousRunData) return true;
    // This comparison scales poorly with # of conditions but vast majority of audiences are < 25 conditions.
    // The backend queries also scale even worse, so we don't expect users to be running waterfalls on complex audiences.
    return hasQueryChanged(previousRunData, nextRunData);
  }, [previousRunData, nextRunData]);

  const {
    audienceSize,
    isLoading,
    calculateSizes,
    cancelCalculateAudienceSize,
  } = useCalculateAudienceSize({
    audienceId: audienceId?.toString(),
    parentModelId: parentModelId?.toString() ?? "",
    subsetIds,
    isFastQuery,
    sourceId: sourceId?.toString() ?? "",
    visualQueryFilter: filter,
    destinationId,
  });

  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && !isCanceledRef.current) {
      calcDidComplete(myId);
      setPreviousRunData(nextRunData);
    }

    wasLoadingRef.current = isLoading;
  }, [isLoading, calcDidComplete, myId, nextRunData]);

  const showLoadingState = isLoading || isQueued;

  const handleCalculateAudienceSize = async () => {
    if (isDirty) {
      isCanceledRef.current = false;
      calculateSizes();
    }
  };

  const handleCancelAudienceCalculation = () => {
    // Only cancel if it is actively calculating
    if (isLoading) {
      isCanceledRef.current = true;
      cancelCalculateAudienceSize();
    }
  };

  // Forward unstable props/state into refs to make registration callbacks stable
  const listenersRef = useRef<AudienceWaterfallListeners>({
    calculate: handleCalculateAudienceSize,
    cancel: handleCancelAudienceCalculation,
    setQueued: setIsQueued,
  });

  listenersRef.current = {
    calculate: handleCalculateAudienceSize,
    cancel: handleCancelAudienceCalculation,
    setQueued: setIsQueued,
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useLayoutEffect(() => {
    const deregister = register(listenersRef, myId, index);
    return () => {
      deregister();
    };
  }, [register, listenersRef, myId, index]);

  const isClickable = isDirty && !showLoadingState && isPermitted;

  const clickableProps = {
    _hover: { color: "link.hover", cursor: "pointer" },
    _focusVisible: { boxShadow: "outline" },
    _active: { color: "link.pressed" },
    onClick: calculateAllWaterfall,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        (e.key === "Enter" || e.key === " ") &&
        isPermitted &&
        !showLoadingState &&
        isDirty
      ) {
        e.preventDefault();
        calculateAllWaterfall();
      }
    },
    tabIndex: 0,
  };

  return (
    <Tooltip
      message="You do not have permission to perform this action."
      isDisabled={isPermitted}
    >
      <Row
        gap={2}
        alignItems="center"
        lineHeight="16px"
        fontWeight="500"
        style={{ fontSize: "12px" }}
        color={
          !isPermitted
            ? "text.disabled"
            : showLoadingState
              ? "text.tertiary"
              : isDirty
                ? "link.default"
                : "text.primary"
        }
        {...(isClickable ? clickableProps : {})}
        aria-disabled={!isPermitted || showLoadingState}
        {...(isDirty && !showLoadingState ? { role: "button" } : {})}
      >
        <Row fontSize="16px" height="16px" width="16px" alignItems="center">
          {showLoadingState ? (
            <LoadingWithHoverCancel
              onCancel={cancelAllWaterfall}
              iconButtonSize="xs"
            />
          ) : isDirty && audienceSize ? (
            <RefreshIcon />
          ) : audienceSize ? (
            <AudienceIcon color="text.secondary" />
          ) : (
            <AudienceIcon />
          )}
        </Row>
        {isLoading
          ? "Calculating…"
          : audienceSize
            ? displayAudienceSize(audienceSize)
            : "Calculate size"}
      </Row>
    </Tooltip>
  );
};

export const AudienceWaterfall = (props: AudienceWaterfallProps) => {
  const { ...rest } = props;
  const waterfallContext = useAudienceWaterfall();
  if (!waterfallContext) {
    return null;
  }
  return <AudienceWaterfallInternal {...rest} {...waterfallContext} />;
};

interface ShouldShowWaterfalProps {
  conditionType: ConditionType.Or | ConditionType.And;
  audienceId?: string | number;
  parentId?: string | number;
  level: number;
}

export const useShouldShowWaterfall = (props: ShouldShowWaterfalProps) => {
  const { parentId, conditionType, level } = props;
  const { audienceWaterfalls } = useFlags();
  const enabled = useContextSelector(
    AudienceWaterfallContext,
    (state) => !!state && state.enabled,
  );
  return (
    enabled &&
    audienceWaterfalls &&
    level === 0 &&
    conditionType === ConditionType.And &&
    !!parentId
  );
};
